import {isAngebot, isGutschrift, isLieferschein, isRechnung} from '../util/typeguards';
import {Belegart} from '../util/belegart.enum';


/**
 * Sprachabhaengige Labels fuer das WYSIWYG-Layout eines Belegs.
 */
export interface BelegLabels {
  // Beleg-Kopf
  belegTitle: string;
  belegTitleKorrektur: string;
  compoundNummer: string;
  nummerLabel: string;
  datumLabel: string;
  leistungsdatum: string;
  kundennummer: string;
  steuernummer: string;
  ustId: string;
  // B2G-Referenzfelder
  bestellnummer: string;
  lieferantennummer: string;
  vertragsnummer: string;
  projektnummer: string;
  leitwegId: string;
  // Positionstabelle
  positionNr: string;
  positionProdukt: string;
  positionMenge: string;
  positionEinheit: string;
  positionUst: string;
  positionEinzelpreisNetto: string;
  positionGesamtpreis: string;
  // Summenblock
  summenNettobetrag: string;
  summenUstZeilePrefix: string;
  summenGesamtbetragBrutto: string;
  // Fusszeile
  fusszeileAnsprechpartner: string;
  fusszeileEmail: string;
  fusszeileTelefon: string;
  fusszeileSteuernummer: string;
  fusszeileUstId: string;
  fusszeileGlaeubigerId: string;
  fusszeileKontoinhaber: string;
  fusszeileIban: string;
  fusszeileBic: string;
  fusszeileVvvoNummer: string;
  fusszeileOekoKontrollnummer: string;
  // Freitext-Defaults / Placeholder
  vorlaufzeilePlaceholder: string;
  vorlaufzeileDefault: string;
  schlussbemerkungDefault: string;
  // Sub-Hinweise im Belegkopf
  nummerAutomatischHint: string;
  // Pflichthinweis-Text bei innergemeinschaftlicher Lieferung
  iglPflichthinweis: string;
  // Referenzzeile auf Vorgaenger der Belegkette, „<referenzZu[belegart]> <Nummer> <referenzVom> <Datum>“
  referenzZu: Record<Belegart, string>;
  referenzVom: string;
}

/**
 * Belegart-spezifische Label-Schluessel. Nur diese Felder unterscheiden sich
 * zwischen den Belegarten; alles Uebrige ist belegart-neutral.
 */
type BelegartLabelKey =
  | 'belegTitle'
  | 'belegTitleKorrektur'
  | 'compoundNummer'
  | 'nummerLabel'
  | 'datumLabel'
  | 'leistungsdatum'
  | 'vorlaufzeileDefault'
  | 'schlussbemerkungDefault';

/** Overlay je Belegart: muss ALLE belegart-spezifischen Labels liefern (compile-exhaustiv via Pick). */
type BelegartOverlay = Pick<BelegLabels, BelegartLabelKey>;

/** Belegart-neutrales Basis-Set: alle gemeinsamen Labels (ohne die belegart-spezifischen). */
type BelegLabelsBase = Omit<BelegLabels, BelegartLabelKey>;

/**
 * Belegart-neutrale Basis-Labels je Sprache. Enthaelt ausschliesslich Labels, die
 * fuer alle Belegarten identisch sind. Belegart-spezifische Labels liefern die Overlays.
 */
const BELEG_LABELS_BASE_DE: BelegLabelsBase = {
  kundennummer: 'Kunden-Nr.',
  steuernummer: 'Steuernummer',
  ustId: 'USt-ID',
  bestellnummer: 'Bestell-Nr.',
  lieferantennummer: 'Lieferanten-Nr.',
  vertragsnummer: 'Vertrags-Nr.',
  projektnummer: 'Projekt-Nr.',
  leitwegId: 'Leitweg-ID',
  positionNr: 'Nr.',
  positionProdukt: 'Produkt',
  positionMenge: 'Menge',
  positionEinheit: 'Einheit',
  positionUst: 'USt',
  positionEinzelpreisNetto: 'Einzelpreis (netto)',
  positionGesamtpreis: 'Gesamtpreis',
  summenNettobetrag: 'Nettobetrag',
  summenUstZeilePrefix: 'USt',
  summenGesamtbetragBrutto: 'Gesamtbetrag (brutto)',
  fusszeileAnsprechpartner: 'Ansprechpartner',
  fusszeileEmail: 'E-Mail',
  fusszeileTelefon: 'Tel.',
  fusszeileSteuernummer: 'Steuernummer',
  fusszeileUstId: 'USt-ID',
  fusszeileGlaeubigerId: 'Gläubiger-ID',
  fusszeileKontoinhaber: 'Kontoinhaber',
  fusszeileIban: 'IBAN',
  fusszeileBic: 'BIC',
  fusszeileVvvoNummer: 'VVVO-Nr.',
  fusszeileOekoKontrollnummer: 'Öko-Kontrollnr.',
  vorlaufzeilePlaceholder: 'Begrüßungstext hinzufügen',
  nummerAutomatischHint: 'Wird autom. vergeben',
  iglPflichthinweis: 'Steuerfreie innergemeinschaftliche Lieferung gemäß § 4 Nr. 1 Buchst. b i. V. m. § 6a UStG.',
  referenzZu: {
    [Belegart.Rechnung]: 'Zu Rechnung',
    [Belegart.Gutschrift]: 'Zu Gutschrift',
    [Belegart.Lieferschein]: 'Zu Lieferschein',
    [Belegart.Angebot]: 'Zu Angebot',
  },
  referenzVom: 'vom',
};

const BELEG_LABELS_BASE_EN: BelegLabelsBase = {
  kundennummer: 'Customer No.',
  steuernummer: 'Tax Number',
  ustId: 'VAT ID',
  bestellnummer: 'Order No.',
  lieferantennummer: 'Supplier No.',
  vertragsnummer: 'Contract No.',
  projektnummer: 'Project No.',
  leitwegId: 'Routing ID',
  positionNr: 'No.',
  positionProdukt: 'Product',
  positionMenge: 'Quantity',
  positionEinheit: 'Unit',
  positionUst: 'VAT',
  positionEinzelpreisNetto: 'Unit Price (net)',
  positionGesamtpreis: 'Total',
  summenNettobetrag: 'Net Amount',
  summenUstZeilePrefix: 'VAT',
  summenGesamtbetragBrutto: 'Total Amount (gross)',
  fusszeileAnsprechpartner: 'Contact',
  fusszeileEmail: 'E-Mail',
  fusszeileTelefon: 'Tel.',
  fusszeileSteuernummer: 'Tax Number',
  fusszeileUstId: 'VAT ID',
  fusszeileGlaeubigerId: 'Creditor ID',
  fusszeileKontoinhaber: 'Account holder',
  fusszeileIban: 'IBAN',
  fusszeileBic: 'BIC',
  fusszeileVvvoNummer: 'VVVO No.',
  fusszeileOekoKontrollnummer: 'Organic Control No.',
  vorlaufzeilePlaceholder: 'Add greeting text',
  nummerAutomatischHint: 'Assigned automatically',
  iglPflichthinweis: 'Tax-exempt intra-Community supply pursuant to Sec. 4 No. 1b in conjunction with Sec. 6a German VAT Act.',
  referenzZu: {
    [Belegart.Rechnung]: 'For invoice',
    [Belegart.Gutschrift]: 'For credit note',
    [Belegart.Lieferschein]: 'For delivery note',
    [Belegart.Angebot]: 'For quotation',
  },
  referenzVom: 'dated',
};

/**
 * Belegart-spezifische Labels je Belegart und Sprache. Der Pick-Typ erzwingt,
 * dass jedes Overlay alle belegart-spezifischen Labels liefert.
 */
export const RECHNUNG_OVERLAY_DE: BelegartOverlay = {
  belegTitle: 'Rechnung',
  belegTitleKorrektur: 'Rechnungskorrektur',
  compoundNummer: 'Rechnungs',
  nummerLabel: 'Rechnungs-Nr.',
  datumLabel: 'Rechnungsdatum',
  leistungsdatum: 'Leistungs-/Lieferdatum',
  vorlaufzeileDefault: 'Vielen Dank für Ihren Auftrag. Wir erlauben uns, die nachfolgenden Positionen zu berechnen:',
  schlussbemerkungDefault: 'Wir danken Ihnen für Ihr Vertrauen und die gute Zusammenarbeit. Wir freuen uns über Ihre Weiterempfehlung.',
};

export const RECHNUNG_OVERLAY_EN: BelegartOverlay = {
  belegTitle: 'Invoice',
  belegTitleKorrektur: 'Invoice Correction',
  compoundNummer: 'Invoice',
  nummerLabel: 'Invoice No.',
  datumLabel: 'Invoice Date',
  leistungsdatum: 'Delivery/Service Date',
  vorlaufzeileDefault: 'Thank you for your order. We hereby invoice the following items:',
  schlussbemerkungDefault: 'We thank you for your trust and the good cooperation. We appreciate your recommendation.',
};

export const GUTSCHRIFT_OVERLAY_DE: BelegartOverlay = {
  belegTitle: 'Gutschrift',
  belegTitleKorrektur: 'Gutschriftskorrektur',
  compoundNummer: 'Gutschrifts',
  nummerLabel: 'Gutschrifts-Nr.',
  datumLabel: 'Gutschriftsdatum',
  leistungsdatum: 'Leistungs-/Lieferdatum',
  vorlaufzeileDefault: 'Vielen Dank für Ihre Leistung. Hiermit vergüten wir Ihnen die nachfolgenden Positionen:',
  schlussbemerkungDefault: 'Wir danken Ihnen für die gute Zusammenarbeit.',
};

export const GUTSCHRIFT_OVERLAY_EN: BelegartOverlay = {
  belegTitle: 'Credit Note',
  belegTitleKorrektur: 'Credit Note Correction',
  compoundNummer: 'Credit Note',
  nummerLabel: 'Credit Note No.',
  datumLabel: 'Credit Note Date',
  leistungsdatum: 'Delivery/Service Date',
  // INFO: Gutschriften sind laut BelegSpracheCalculator immer DE; diese EN-Werte werden praktisch nicht erreicht.
  vorlaufzeileDefault: 'Thank you for your service. We hereby credit you the following items:',
  schlussbemerkungDefault: 'We thank you for the good cooperation.',
};

export const LIEFERSCHEIN_OVERLAY_DE: BelegartOverlay = {
  belegTitle: 'Lieferschein',
  belegTitleKorrektur: 'Lieferscheinkorrektur',
  compoundNummer: 'Lieferschein',
  nummerLabel: 'Lieferschein-Nr.',
  datumLabel: 'Lieferscheindatum',
  leistungsdatum: 'Lieferdatum',
  vorlaufzeileDefault: 'Vielen Dank für Ihren Auftrag. Wir liefern Ihnen die nachfolgend aufgeführten Positionen:',
  schlussbemerkungDefault: 'Bitte prüfen Sie die Lieferung bei Erhalt auf Vollständigkeit und Unversehrtheit. Wir danken Ihnen für Ihr Vertrauen und die gute Zusammenarbeit.',
};

export const LIEFERSCHEIN_OVERLAY_EN: BelegartOverlay = {
  belegTitle: 'Delivery Note',
  belegTitleKorrektur: 'Delivery Note Correction',
  compoundNummer: 'Delivery Note',
  nummerLabel: 'Delivery Note No.',
  datumLabel: 'Delivery Note Date',
  leistungsdatum: 'Delivery Date',
  vorlaufzeileDefault: 'Thank you for your order. We hereby deliver the following items:',
  schlussbemerkungDefault: 'Please check the delivery for completeness and integrity upon receipt. We thank you for your trust and the good cooperation.',
};

// INFO: leistungsdatum ist Pflicht im Overlay-Typ, wird beim Angebot aber nicht angezeigt.
export const ANGEBOT_OVERLAY_DE: BelegartOverlay = {
  belegTitle: 'Angebot',
  belegTitleKorrektur: 'Angebotskorrektur',
  compoundNummer: 'Angebots',
  nummerLabel: 'Angebots-Nr.',
  datumLabel: 'Angebotsdatum',
  leistungsdatum: 'Leistungs-/Lieferdatum',
  vorlaufzeileDefault: 'Vielen Dank für Ihre Anfrage. Gerne bieten wir Ihnen die nachfolgenden Positionen an:',
  schlussbemerkungDefault: 'Wir freuen uns, wenn unser Angebot Ihre Zustimmung findet, und stehen Ihnen für Rückfragen gerne zur Verfügung.',
};

export const ANGEBOT_OVERLAY_EN: BelegartOverlay = {
  belegTitle: 'Quotation',
  belegTitleKorrektur: 'Quotation Correction',
  compoundNummer: 'Quotation',
  nummerLabel: 'Quotation No.',
  datumLabel: 'Quotation Date',
  leistungsdatum: 'Delivery/Service Date',
  vorlaufzeileDefault: 'Thank you for your enquiry. We are pleased to offer you the following items:',
  schlussbemerkungDefault: 'We hope our quotation meets your requirements and look forward to your order.',
};

/**
 * Liefert ein vollstaendiges Label-Set zur Kombination aus Sprache und Belegart.
 * Sprache: alles ausser 'EN' faellt auf Deutsch zurueck.
 * Belegart: RECHNUNG/GUTSCHRIFT/LIEFERSCHEIN/ANGEBOT je eigenes Overlay; unbekannte oder
 * fehlende Belegart faellt auf die Rechnungs-Variante zurueck.
 * Erzeugt bei jedem Aufruf ein frisches Objekt - es wird kein veraenderbarer Zustand
 * zwischen Aufrufern geteilt; die Referenz-Stabilitaet uebernimmt der belegLabels-Selektor.
 *
 * @param sprache Sprach-Code vom Backend ('DE' | 'EN' | undefined | null)
 * @param belegart optionale Belegart, um das passende Overlay zu waehlen
 * @returns frisches Label-Set
 */
export function getBelegLabels(sprache?: string | null, belegart?: string | null): BelegLabels {
  const isEn = sprache === 'EN';
  const base = isEn ? BELEG_LABELS_BASE_EN : BELEG_LABELS_BASE_DE;

  if (sprache != null && sprache !== 'DE' && sprache !== 'EN') {
    console.warn(`getBelegLabels: unbekannte Sprache '${sprache}', Fallback auf DE.`);
  }

  if (isRechnung(belegart ?? undefined)) {
    return {...base, ...(isEn ? RECHNUNG_OVERLAY_EN : RECHNUNG_OVERLAY_DE)};
  }

  if (isGutschrift(belegart ?? undefined)) {
    return {...base, ...(isEn ? GUTSCHRIFT_OVERLAY_EN : GUTSCHRIFT_OVERLAY_DE)};
  }

  if (isLieferschein(belegart ?? undefined)) {
    return {...base, ...(isEn ? LIEFERSCHEIN_OVERLAY_EN : LIEFERSCHEIN_OVERLAY_DE)};
  }

  if (isAngebot(belegart ?? undefined)) {
    return {...base, ...(isEn ? ANGEBOT_OVERLAY_EN : ANGEBOT_OVERLAY_DE)};
  }

  // Fallback: unbekannte/fehlende Belegart -> Rechnung; fehlend ist beim initialen Laden normal
  if (belegart) {
    console.warn(`getBelegLabels: unbekannte Belegart '${belegart}', Fallback auf Rechnung.`);
  }
  return {...base, ...(isEn ? RECHNUNG_OVERLAY_EN : RECHNUNG_OVERLAY_DE)};
}

/**
 * Standard-Label-Sets als Default-Initialwert fuer Komponenten und als Referenzwerte in Tests.
 * Einmal beim Modul-Load erzeugt; nur lesend verwenden.
 */
export const BELEG_LABELS_RECHNUNG_DE: BelegLabels = getBelegLabels('DE');
export const BELEG_LABELS_RECHNUNG_EN: BelegLabels = getBelegLabels('EN');
export const BELEG_LABELS_GUTSCHRIFT_DE: BelegLabels = getBelegLabels('DE', 'GUTSCHRIFT');
export const BELEG_LABELS_LIEFERSCHEIN_DE: BelegLabels = getBelegLabels('DE', 'LIEFERSCHEIN');
